<template>
  <section class="continue-watching-rail" :style="railStyle">
    <GlyphView :rail="rail" />
    <div class="title-container">
      <h3 class="rail-title" v-if="title">{{ title }}</h3>
      <p class="rail-subtitle" v-if="subtitle">{{ subtitle }}</p>
    </div>

    <div v-if="!hasItems">
      <!-- If Error -->
      <div v-if="isError" class="error-container">
        <p class="state-message">{{ $t('home.loadFailed') }}</p>
        <TG4Button
          :id="firstItemId"
          v-press="load"
          v-focus
          primary
          :title="$t('general.retry')"
          @up="handleUp"
          @down="handleDown"
          @left="handleLeftFallback"/>
      </div>
      <!-- If loading or empty -->
      <DynamicRailLoadingState
        v-else
        :id="firstItemId"
        :loading="isLoading"
        :message="String($t('home.continueWatchingEmpty'))"
        tabindex="0"
        v-focus
        @up="handleUp"
        @down="handleDown"
        @left="handleLeftFallback"/>
    </div>

    <HorizontalScroller
      v-else
      :container-id="containerId"
      :items-prefix="`rail-${props.railIndex}`"
      :videos="CMSContinueWatchingManager.videos.value"
      @handleUp="handleUp"
      @handleDown="handleDown"
      @handleLeftFallback="handleLeftFallback"
    />
  </section>
</template>

<script setup lang="ts">


import { computed, onMounted, nextTick, watch } from 'vue';
import { RailResponse } from '@/types/cms';
import { Language } from '@/enums/language';
import { useStore } from '@/composables/useStore';
import { useRailNavigation } from '@/composables/useRailNavigation';
import { CMSContinueWatchingManager } from '@/services/CMSContinueWatchingManager';
import HorizontalScroller from '@/components/containers/HorizontalScroller.vue';
import DynamicRailLoadingState from '@/components/rails/DynamicRailLoadingState.vue';
import TG4Button from '@/components/TG4Button.vue';
import { navigationService } from 'vue-spatialnavigation';
import GlyphView  from '@/components/glyphs/GlyphView.vue';

const props = defineProps<{
  rail: RailResponse;
  railIndex: number;
  railDidNavigateDown?: (railIndex: number) => void;
}>();


const { containerId, itemId, handleUp, handleDown, handleLeftFallback } = useRailNavigation(props);

const { useGetter } = useStore();
const currentLanguage = useGetter<string>('currentLanguage');

const localised = (en: string | null, ga: string | null) => {
  const value = currentLanguage.value === Language.Irish ? ga : en;
  return value ?? en ?? '';
};

const title = computed(() => localised(props.rail.titleEn, props.rail.titleGa));
const subtitle = computed(() => localised(props.rail.subtitleEn, props.rail.subtitleGa));

const railStyle = computed(() => ({
  'background-color': props.rail.backgroundColor ?? undefined,
  color: props.rail.textColor ?? undefined,
}));

const firstItemId = computed(() => itemId(0));



const hasItems = computed(() => (CMSContinueWatchingManager.videos.value?.length ?? 0) > 0);
const isLoading = computed(() => CMSContinueWatchingManager.state.value === 'loading');
const isError = computed(() => CMSContinueWatchingManager.state.value === 'error');

const load = () => CMSContinueWatchingManager.loadContinueWatchingRail();

onMounted(() => {
  //Manager is a singleton, so returning to home does not refetch
  if (CMSContinueWatchingManager.videos.value === undefined) load();
});

//Move focus to the first card when the state message it was on unmounts
watch(hasItems, async (nowHasItems) => {
  if (!nowHasItems) return;
  //Library's focus, not activeElement — it skips native focus when tabIndex is -1
  if (navigationService.getFocusElementInFocus()?.id !== firstItemId.value) return;

  await nextTick();
  // Small delay so v-focus has registered the newly inserted cell.
  setTimeout(() => {
    const focusElement = navigationService.getFocusElementById(firstItemId.value);
    focusElement?.focus();
    document.getElementById(firstItemId.value)?.focus();
  }, 100);
});
</script>

<style scoped lang="scss">
.continue-watching-rail {
  position: relative;
  z-index: 0;
  padding-top: 5px;
  padding-bottom: 40px;
  padding-left: $rail-padding-left;
  padding-right: $rail-padding-right;
}

.title-container {
  text-align: left;
}

.rail-title {
  @include text-rail-title;
}

.rail-subtitle {
  @include text-rail-subtitle;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  //Matches the loading/empty state so the row does not change height
  min-height: 195px;
}

.state-message {
  margin: 0 0 20px 0;
  font-size: 30px;
  font-weight: 500;
  color: $color-off-white;
  text-align: center;
}
</style>
